// /plan/airframes — library of sim_airframes with filters + actions.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Filter, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatApiError } from "@/lib/formatApiError";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import CredibilityBadge from "@/components/CredibilityBadge";
import { sim } from "@/api/endpoints";
import { api as apiClient } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

// ---------------------------------------------------------------------------
// Phase 17.8 — Performance badges (cruise / accel / climb) + calibrate button.
// ---------------------------------------------------------------------------
function PerformanceBadges({ afId }) {
    const [perf, setPerf] = useState(null);
    const [calibrating, setCalibrating] = useState(false);
    const load = useCallback(async () => {
        try {
            const r = await apiClient.get(`/api/sim/airframes/${afId}/performance`);
            setPerf(r.data || {});
        } catch (e) {
            setPerf({ __err: formatApiError(e) });
        }
    }, [afId]);
    useEffect(() => { if (afId) load(); }, [afId, load]);
    const calibrate = async () => {
        setCalibrating(true);
        try {
            const r = await apiClient.post(`/api/sim/airframes/${afId}/calibrate`);
            setPerf(r.data || {});
            toast.success("Calibration complete");
        } catch (e) {
            toast.error(`Calibration failed: ${formatApiError(e)}`);
        } finally {
            setCalibrating(false);
        }
    };
    if (!perf) return null;
    if (perf.__err) {
        return <div className="text-[10px] text-ux-text-3"
                    data-testid={`airframe-perf-error-${afId}`}>perf: {perf.__err}</div>;
    }
    const ana = perf.analytical || {};
    const sim_ = perf.simulated;
    const Badge = ({ label, ana_val, sim_val, unit, testId }) => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="rounded border border-ux-border-1 bg-ux-bg-2 px-2 py-1 text-[11px] tabular-nums"
                          data-testid={testId}>
                        <span className="text-ux-text-3">{label}</span>{" "}
                        <span className="text-ux-text-1 font-medium">
                            {(sim_val ?? ana_val ?? 0).toFixed(1)}
                        </span>
                        <span className="text-ux-text-3 ml-0.5">{unit}</span>
                        {sim_val != null && (
                            <span className="ml-1 text-[9px] text-emerald-300">sim</span>
                        )}
                    </span>
                </TooltipTrigger>
                <TooltipContent className="text-[11px]">
                    Analytical: {ana_val?.toFixed?.(2) ?? "—"} {unit}
                    {sim_val != null && <><br />Simulated: {sim_val.toFixed(2)} {unit}</>}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
    return (
        <div className="flex flex-wrap items-center gap-1.5"
             data-testid={`airframe-perf-${afId}`}>
            <Badge label="cruise" ana_val={ana.cruise_mps} sim_val={sim_?.cruise_mps}
                   unit="m/s" testId={`airframe-perf-cruise-${afId}`} />
            <Badge label="accel" ana_val={ana.accel_mps2} sim_val={sim_?.accel_mps2}
                   unit="m/s²" testId={`airframe-perf-accel-${afId}`} />
            <Badge label="climb" ana_val={ana.climb_mps} sim_val={sim_?.climb_mps}
                   unit="m/s" testId={`airframe-perf-climb-${afId}`} />
            <Button size="sm" variant="ghost"
                    className="h-6 px-2 text-[10px]"
                    onClick={calibrate} disabled={calibrating}
                    data-testid={`airframe-perf-calibrate-${afId}`}>
                {calibrating
                    ? <><Loader2 className="mr-1 size-3 animate-spin" /> calibrating…</>
                    : "calibrate"}
            </Button>
        </div>
    );
}

const CLASSES = [
    { id: "", label: "All classes" },
    { id: "multirotor", label: "Multirotor" },
    { id: "fixed_wing", label: "Fixed-wing" },
    { id: "loitering_munition", label: "Loitering munition" },
    { id: "interceptor_kinetic", label: "Interceptor (kinetic)" },
    { id: "interceptor_net", label: "Interceptor (net)" },
];
const ROLES = [
    { id: "", label: "Both roles" },
    { id: "threat", label: "Threat" },
    { id: "interceptor", label: "Interceptor" },
];
const SOURCES = [
    { id: "all", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

function StateChip({ state }) {
    const cls =
        state === "verified"
            ? "bg-ux-success/10 text-ux-success border-ux-success/30"
            : state === "failed"
            ? "bg-ux-danger/10 text-ux-danger border-ux-danger/30"
            : state === "warnings"
            ? "bg-ux-warning/10 text-ux-warning border-ux-warning/30"
            : state === "running"
            ? "bg-ux-accent/10 text-ux-accent border-ux-accent/30"
            : "bg-ux-bg-2 text-ux-text-3 border-ux-border-1";
    return (
        <span className={`inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${cls}`}>
            {state || "unverified"}
        </span>
    );
}

export default function AirframesPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [filters, setFilters] = useState({ class_: "", role: "", source: "all" });
    const [verifyingId, setVerifyingId] = useState(null);
    const [cloningId, setCloningId] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const scope =
                filters.source === "mine" ? "mine" : filters.source === "preset" ? "preset" : "tenant";
            const data = await sim.airframes.list({
                scope,
                class_: filters.class_ || undefined,
                role: filters.role || undefined,
            });
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load airframes"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const visible = useMemo(() => {
        // Client-side belt-and-braces filter — the server already filters
        // by class / role, but source = "all" returns both preset + tenant.
        return items;
    }, [items]);

    const openWorkbench = async (af) => {
        try {
            const doc = await sim.scenarios.create({
                name: `Scenario for ${af.name}`,
                description: `Seeded from airframe ${af.id}`,
            });
            await sim.scenarios.attachAirframe(doc.id, {
                airframe_id: af.id,
                role: af.role,
            });
            navigate(`/workbench/${doc.id}#section=airframe`);
        } catch (e) {
            toast.error(formatApiError(e, "Could not open in Workbench"));
        }
    };

    const runVerify = async (af) => {
        setVerifyingId(af.id);
        try {
            const fresh = await sim.airframes.verify(af.id);
            setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
            toast.success(
                `Verify: ${fresh.verification?.state}` +
                    (fresh.verification?.backend_used ? ` (${fresh.verification.backend_used})` : ""),
            );
        } catch (e) {
            toast.error(formatApiError(e, "Verify failed"));
        } finally {
            setVerifyingId(null);
        }
    };

    const cloneToTenant = async (af) => {
        setCloningId(af.id);
        try {
            const cloned = await sim.airframes.clone(af.id);
            toast.success(`Cloned "${af.name}" → "${cloned.name}"`);
            // Drop the new clone into the Workbench so the operator
            // can start editing immediately — matches the "Open in
            // Workbench" pattern already used elsewhere on this page.
            await openWorkbench(cloned);
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setCloningId(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="airframes-page">
            <PageHeader
                title="Airframes"
                subtitle="Platforms — presets and your own — feeding the Simulation Workbench."
                actions={
                    <div className="flex gap-2">
                        <Link to="/plan/airframes/new">
                            <Button
                                size="sm"
                                variant="outline"
                                data-testid="airframes-new"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" /> New airframe
                            </Button>
                        </Link>
                        <Link to="/plan/airframes/import">
                            <Button
                                size="sm"
                                data-testid="airframes-import"
                            >
                                <Upload className="h-3.5 w-3.5 mr-1" /> Import JSBSim XML
                            </Button>
                        </Link>
                    </div>
                }
            />

            {/* Filters row */}
            <div
                className="flex flex-wrap items-center gap-2 border border-ux-border-1 rounded-md p-2 bg-ux-bg-1"
                data-testid="airframes-filters"
            >
                <Filter className="w-4 h-4 text-ux-text-3" />
                <Select
                    value={filters.class_ || "__all__"}
                    onValueChange={(v) => setFilters((f) => ({ ...f, class_: v === "__all__" ? "" : v }))}
                >
                    <SelectTrigger className="w-[180px] bg-ux-bg-2 text-[12px] font-mono" data-testid="filter-class">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {CLASSES.map((c) => (
                            <SelectItem key={c.id || "__all__"} value={c.id || "__all__"} className="text-[12px] font-mono">
                                {c.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={filters.role || "__all__"}
                    onValueChange={(v) => setFilters((f) => ({ ...f, role: v === "__all__" ? "" : v }))}
                >
                    <SelectTrigger className="w-[160px] bg-ux-bg-2 text-[12px] font-mono" data-testid="filter-role">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {ROLES.map((r) => (
                            <SelectItem key={r.id || "__all__"} value={r.id || "__all__"} className="text-[12px] font-mono">
                                {r.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={filters.source}
                    onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}
                >
                    <SelectTrigger className="w-[160px] bg-ux-bg-2 text-[12px] font-mono" data-testid="filter-source">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {SOURCES.map((s) => (
                            <SelectItem key={s.id} value={s.id} className="text-[12px] font-mono">
                                {s.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="ml-auto text-[11px] font-mono text-ux-text-3" data-testid="airframes-count">
                    {loading ? "loading…" : `${visible.length} airframe${visible.length === 1 ? "" : "s"}`}
                </span>
            </div>

            {err && (
                <div className="text-ux-danger text-[13px]" role="alert" data-testid="airframes-error">
                    {err}
                </div>
            )}

            {!loading && visible.length === 0 && (
                <EmptyState
                    title="No airframes"
                    body="Create a new airframe or import a JSBSim XML to get started."
                />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {visible.map((af) => (
                    <div
                        key={af.id}
                        className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 flex flex-col gap-2"
                        data-testid={`airframe-card-${af.id}`}
                    >
                        <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-[14px] font-medium text-ux-text-1 truncate">{af.name}</h3>
                                <p className="text-[11px] font-mono text-ux-text-3 truncate">
                                    {af.class} · {af.role} · v{af.version}
                                </p>
                            </div>
                            <CredibilityBadge entity={af} testId={`airframe-tier-${af.id}`} />
                            <StateChip state={af.verification?.state} />
                        </div>
                        <p className="text-[12px] text-ux-text-2 line-clamp-2 min-h-[32px]">
                            {af.notes || "—"}
                        </p>
                        <PerformanceBadges afId={af.id} />
                        <div className="flex flex-wrap gap-2 pt-1">
                            <SpinnerButton
                                size="sm"
                                variant="outline"
                                loading={verifyingId === af.id}
                                onClick={() => runVerify(af)}
                                testId={`airframe-verify-${af.id}`}
                            >
                                Verify
                            </SpinnerButton>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openWorkbench(af)}
                                data-testid={`airframe-open-workbench-${af.id}`}
                            >
                                Open in Workbench
                            </Button>
                            {af.is_preset && (
                                <SpinnerButton
                                    size="sm"
                                    variant="outline"
                                    loading={cloningId === af.id}
                                    onClick={() => cloneToTenant(af)}
                                    testId={`airframe-clone-${af.id}`}
                                >
                                    Clone to my tenant
                                </SpinnerButton>
                            )}
                            <PresetPublishButton
                                entityType="airframes"
                                entityId={af.id}
                                entity={af}
                                user={user}
                                size="sm"
                                variant="outline"
                                testId={`airframe-publish-${af.id}`}
                                onPublished={load}
                            />
                            <a
                                href={sim.airframes.exportXmlUrl(af.id)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[12px] text-ux-accent ml-auto self-center"
                                data-testid={`airframe-export-${af.id}`}
                            >
                                Export XML
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
